import { createDocRef, getDocument } from 'lib/firebase/utils';
import { T_UserSubscriptionDetails } from 'services/subscription/interface';

import useSWR from 'swr';

export interface GetUserSubscriptionDetailsKeyParams {
  userId?: string;
}

interface GetUserSubscriptionDetailsParams {
  userId: string;
}

export const getUserSubscriptionDetailsKey = ({
  userId
}: GetUserSubscriptionDetailsKeyParams) => {
  return userId ? `/users/${userId}/subscriptionDetails` : null;
};

export const getUserSubscriptionDetails = async ({
  userId
}: GetUserSubscriptionDetailsParams): Promise<T_UserSubscriptionDetails | null> => {
  const subscriptionDocRef = await createDocRef(
    `users/${userId}/private/subscription`
  );

  return getDocument(subscriptionDocRef).then(doc => {
    if (!doc.exists) {
      return null;
    }

    const subscriptionDetails = doc.data() as T_UserSubscriptionDetails;

    return subscriptionDetails ?? null;
  });
};

export const useGetUserSubscriptionDetails = ({
  userId
}: {
  userId?: string;
}) => {
  const userSubscriptionKey = getUserSubscriptionDetailsKey({ userId });
  const {
    data: userSubscriptionDetails,
    isValidating,
    error,
    revalidate,
    mutate
  } = useSWR(
    userSubscriptionKey,
    () => {
      if (!userId) {
        return null;
      }

      return getUserSubscriptionDetails({ userId });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      shouldRetryOnError: false
    }
  );
  const isSubscriptionDetailsLoading = !!(
    userId &&
    isValidating &&
    !userSubscriptionDetails
  );

  return {
    userSubscriptionDetails,
    isSubscriptionDetailsLoading,
    error,
    revalidate,
    mutate
  };
};

export default useGetUserSubscriptionDetails;
